import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAppContext, useNowContext } from "../App.jsx";
import {
  token_locpath,
  tokenacc_locpath,
  useMetaMaskContext,
  page_bypass_fn,
  tokenaumode_locpath,
  tokenauth_locpath,
} from "./MetaMaskWrapper.jsx";
import {
  q_auth_verify_token,
  q_profile_info,
  q_vaultbikes,
  q_vaultinfo,
  qiserr,
} from "../queries/queries.jsx";
import { useQueries } from "@tanstack/react-query";
import { cdelay, getv, jparse, jstr, nils } from "../utils/utils.jsx";
import Layout from "../components/Layout.jsx";
import { backdropimg } from "../utils/links.jsx";
import { useThirdWebLoginContext } from "../views/ThirdWebLogin.jsx";
import _ from "lodash";
// Add imports for the AuthReminder component
import { Modal } from "../components/Modal.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { twMerge } from "tailwind-merge";
import { Tag } from "../components/utilityComps.jsx";
import { ThridWebLoginSection } from "../views/ThirdWebLogin.jsx";

const AuthContext = createContext();
export const useAuthContext = () => useContext(AuthContext);

export const get_auth_header = () => {
  let token = localStorage.getItem(token_locpath);
  let vault = localStorage.getItem(tokenacc_locpath);
  let aumode = localStorage.getItem(tokenaumode_locpath);
  if (vault) vault = vault.toLowerCase();
  return {
    "x-token": nils(token) ? null : token,
    "x-vault": nils(vault) ? null : vault,
    "x-aumode": nils(aumode) ? null : aumode,
  };
};

export const NoAuthBackdrop = (props) => {
  return (
    <div
      style={{
        height: `92vh`,
        background: `url(${backdropimg})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backdropFilter: "blur(10px)",
      }}
      className="w-[100vw] overflow-hidden"
    >
      {props.children}
    </div>
  );
};

// Inline Login Section (no modal) - can be embedded directly in pages
export const InlineLoginSection = ({
  showVaultDetails = false,
  postlogin,
  customHeading = "Please Login to Continue",
  customDescription = "You will experience limited functionality if you do not login. Please connect your wallet to access all features.",
  customContent = null,
}) => {
  const t3con = useThirdWebLoginContext();
  const aucon = useAuthContext();
  const { aumode, vaultdoc } = aucon;

  return (
    <div className="xs:p-3 sm:p-4 md:p-6">
      {showVaultDetails ? (
        // Show vault details after successful login
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-green-500 text-4xl"
            />
          </div>
          <div className="text-green-400 font-digi text-lg">
            Successfully Logged In!
          </div>
          {vaultdoc && (
            <div className="bg-acc1/10 border border-acc1/30 rounded-lg p-4">
              <div className="text-acc0 font-digi text-sm space-y-2">
                <div>Vault: {vaultdoc.vault_name}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Show login reminder
        <div className="space-y-6">
          {/* Warning Icon and Title */}
          <div className="text-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-orange-500 text-3xl mb-3"
            />
            <h3 className="text-acc0 font-digi text-lg">
              {customHeading}
            </h3>
          </div>

          {/* Login Buttons */}
          <div className="space-y-4">
            {(aumode === "thirdweb" || !nils(t3con.active_account)) && (
              <div className="flex justify-center gap-3">
                <Tag
                  onClick={() => {
                    t3con.on_connect_fn(t3con.active_wallet);
                  }}
                  className={twMerge(
                    "fr-sc resp-gap-1 bg-orange-400/20 text-orange-500 cursor-pointer hover:bg-orange-400/30",
                  )}
                >
                  <FontAwesomeIcon icon={faSync} className="" />
                  <div className="resp-text--1">Retry</div>
                </Tag>
              </div>
            )}

            <div className="flex justify-center">
              <div className="scale-75">
                <ThridWebLoginSection
                  btntext="Login"
                  postlogin={postlogin || (async () => {
                    await aucon.auth_refresh();
                  })}
                />
              </div>
            </div>
          </div>

          {/* Custom Content Below Login Buttons */}
          {customContent && (
            <div>
              {customContent}
            </div>
          )}

          {/* Limited Functionality Warning (only if no custom content) */}
          {!customContent && customDescription && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="text-yellow-400 font-digi text-sm text-center">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="mr-2"
                />
                {customDescription}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const LoginPopup = ({ isOpen, onClose, showVaultDetails = false }) => {
  return (
    <Modal
      is_open={isOpen}
      on_close={onClose}
      title={showVaultDetails ? "Welcome!" : "Login Reminder"}
      size="md"
      className="bg-r2dark/95 border border-acc4/50"
      floating_close={true}
      hide_titlebar={false}
    >
      <InlineLoginSection showVaultDetails={showVaultDetails} />
    </Modal>
  );
};

export const AuthReminder = ({ manual_loginpop, set_manual_loginpop }) => {
  const aucon = useAuthContext();
  const { auth } = aucon;

  const init_delay = 6 * 1e3;
  const [init_lock, set_init_lock] = useState(true);
  const [modal_open, set_modal_open] = useState(false);
  const [show_vault_details, set_show_vault_details] = useState(false);
  const [prev_auth, set_prev_auth] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      set_init_lock(false);
    }, init_delay);
  }, []);

  const show_reminder = useMemo(() => {
    if (init_lock) return false;
    if (auth == true) return false;
    return true;
  }, [auth, init_lock]);

  // Open modal when reminder should show or manual popup is triggered
  useEffect(() => {
    if (show_reminder || manual_loginpop) {
      set_modal_open(true);
    } else {
      setTimeout(() => {
        set_modal_open(false);
      }, 2000);
    }
  }, [show_reminder, manual_loginpop]);

  // Watch for successful login
  useEffect(() => {
    if (prev_auth === false && auth === true && modal_open) {
      // User just logged in, show vault details for 2 seconds
      set_show_vault_details(true);
      setTimeout(() => {
        set_show_vault_details(false);
        set_modal_open(false);
        if (set_manual_loginpop) set_manual_loginpop(false);
      }, 2000);
    }
    set_prev_auth(auth);
  }, [auth, modal_open, prev_auth, set_manual_loginpop]);

  if (!show_reminder && !show_vault_details && !manual_loginpop) return null;

  const close_modal = () => {
    set_modal_open(false);
    if (set_manual_loginpop) set_manual_loginpop(false);
  };

  return (
    <LoginPopup
      isOpen={modal_open}
      onClose={close_modal}
      showVaultDetails={show_vault_details}
    />
  );
};

function AuthWrapper(props) {
  const { now } = useNowContext();

  const { path, history } = useAppContext();
  const mmcon = useMetaMaskContext();
  const t3con = useThirdWebLoginContext();

  const [manual_loginpop, set_manual_loginpop] = useState(false);

  const bypass = useMemo(() => {
    if (page_bypass_fn(path)) return true;
    return false;
  }, [path]);

  const block_reminder = useMemo(() => {
    if (path == "/") return true;
    return false;
  }, [path]);

  useEffect(() => {
    if (!window.ethereum) return;
    if (!mmcon.mm_connected) return;

    window.ethereum.on("accountsChanged", () => {
      // console.log("accountsChanged");
      set_refreshc(refreshc + 1);
    });
  }, [mmcon.mm_connected]);

  const [refreshc, set_refreshc] = useState(0);
  // useEffect(() => { console.log("refreshc", refreshc); }, [refreshc]);
  const grab_loc_cache = () => {
    let aumode = localStorage.getItem(tokenaumode_locpath);

    if (aumode == "wallet") {
      if (!window.ethereum) return [null, null, null];
      const fn = () => {
        let account = window.ethereum.selectedAddress;
        if (!nils(account)) account = account.toLowerCase();
        let token = mmcon.get_token();
        // console.log("refreshc", refreshc, account, token);
        return [aumode, account, token];
      };
      return fn();
    } else if (aumode == "thirdweb") {
      if (!t3con.connected) return [null, null, null];
      let account = localStorage.getItem(tokenacc_locpath);
      let token = localStorage.getItem(token_locpath);
      // console.log("thirdweb", { account, token, con: t3con.connected });
      if (!token || !account) {
        localStorage.removeItem(tokenauth_locpath);
        return [aumode, null, null];
      }

      return [aumode, account, token];
    } else return [null, null, null];
  };
  const [aumode, account, token, vsitorId] = useMemo(() => {
    let cac_resp = grab_loc_cache();
    let resp = jparse(localStorage.getItem(`fingerprint`), {});
    cac_resp.push(getv(resp, "visitorId"));
    return cac_resp;
  }, [refreshc, t3con.connected]);

  useEffect(() => {
    if (!account && bypass == true) {
      setTimeout(() => {
        set_refreshc(refreshc + 1);
      }, 1000);
    }
  }, [bypass]);

  const vault = account && account.toLowerCase();

  const qo_enabled = !nils(vault) && !nils(token) && !nils(aumode);
  const [qo_auth] = useQueries({ queries: [{
      queryKey: ["auth", jstr({ vault, token, aumode })],
      queryFn: () => {
        return q_auth_verify_token({ vault, token, vsitorId });
      },
      staleTime: 5 * 60 * 1e3,
      enabled: qo_enabled,
    },] });

  const auth_refresh = async () => {
    // console.log("auth_refresh");
    set_refreshc(refreshc + 1);
    await cdelay(500);
    await qo_auth.refetch();
  };
  useEffect(() => {
    window.auth_refresh = auth_refresh;
  }, [auth_refresh]);

  const logout = async () => {
    localStorage.removeItem(token_locpath);
    localStorage.removeItem(tokenacc_locpath);
    localStorage.removeItem(tokenaumode_locpath);
    localStorage.removeItem(tokenauth_locpath);
    await auth_refresh();
  };

  const [auth, autherr] = useMemo(() => {
    // console.log("qo_auth", qo_enabled, qo_auth);
    if (!qo_enabled) return [false, "couldnt find auth details"];

    let err = qiserr(qo_auth);

    if (err) {
      // history("/");
      localStorage.removeItem(token_locpath);
      localStorage.removeItem(tokenacc_locpath);
      return [false, err];
    }
    let auth = getv(qo_auth, "data.result.auth");
    let isempty = _.isEmpty(qo_auth?.data);
    if (isempty) return [false, "---"];
    // console.log("auth", auth);

    if (auth) {
      localStorage.setItem(tokenauth_locpath, "true");
      return [true, null];
    } else {
      localStorage.setItem(tokenauth_locpath, "false");
      return [false, `couldn't authenticate`];
    }
  }, [qo_auth.dataUpdatedAt, aumode, t3con.connected]);

  useEffect(() => {
    if (qo_auth.isLoading) return;
    if (auth == false && bypass == false) {
      history("/");
    }
  }, [auth, autherr, qo_auth.dataUpdatedAt]);

  useEffect(() => {
    window.auth_logout = logout;
  }, [logout]);

  const [init_lock, set_init_lock] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      set_init_lock(false);
    }, 10 * 1e3);
  }, []);

  useEffect(() => {
    // console.log({ init_lock });
    if (init_lock) return;
    if (aumode !== "thirdweb") return;
    // console.log("manager", { auth, aumode, t3con: t3con.connected, init_lock });
  }, [auth, aumode, t3con.connected, init_lock]);

  const [qo_vaultinfo, qo_profileinfo, qo_vaultbikes] = useQueries({ queries: [q_vaultinfo({ vault }, { enabled: auth == true }),
    q_profile_info({ vault }, { enabled: auth == true }),
    q_vaultbikes({ vault }, { enabled: auth == true }),] });
  const vaultdoc = useMemo(() => {
    let vaultdoc = getv(qo_vaultinfo, "data.result");
    return vaultdoc;
  }, [qo_vaultinfo.dataUpdatedAt]);
  const profiledoc = useMemo(() => {
    let profiledoc = getv(qo_profileinfo, "data.result");
    return profiledoc;
  }, [qo_profileinfo.dataUpdatedAt]);

  const vaultbikes = useMemo(() => {
    let vaultbikes = getv(qo_vaultbikes, "data.result") ?? [];
    return vaultbikes;
  }, [qo_vaultbikes.dataUpdatedAt]);

  const sel_correct_chain = async () => {
    try {
      const corr_chainId = 137;
      const corr_chainId_hex = "0x" + corr_chainId.toString(16);

      const mm = window.ethereum;
      if (nils(mm)) return;
      let chainIdHex = await mm.request({ method: "eth_chainId" });
      const chainId = parseInt(chainIdHex, 16);
      if (chainId !== corr_chainId) {
        await mm.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: corr_chainId_hex }],
        });
      }
    } catch (err) {}
  };
  useEffect(() => {
    if (auth == true) {
      sel_correct_chain();
    }
  }, [auth]);
  // console.log("auth", { auth, vault });
  //
  //
  const get_contract_init_params = () => {
    if (aumode == "thirdweb") {
      return { aumode, active_account: t3con.active_account };
    } else {
      return { aumode };
    }
  };

  const open_loginpop = async () => {
    console.log("open_loginpop", { manual_loginpop });
    set_manual_loginpop(true);
  };

  // useEffect(() => { console.log({ auth }); }, [auth]);
  // console.log("auth", { auth, vault, vaultdoc });

  const aucon = {
    auth,
    account,
    vault,

    token,
    loading: qo_auth.isLoading,
    auth_refresh,
    logout,

    qo_auth,

    qo_vaultinfo,
    vaultdoc,

    qo_profileinfo,
    profiledoc,

    qo_vaultbikes,
    vaultbikes,

    aumode,
    get_contract_init_params,
    open_loginpop,
  };

  return (
    <AuthContext.Provider value={aucon}>
      {(manual_loginpop || block_reminder == false) && (
        <AuthReminder
          manual_loginpop={manual_loginpop}
          set_manual_loginpop={set_manual_loginpop}
        />
      )}
      {bypass == true ? (
        props.children
      ) : (
        <>
          {auth == true ? (
            props.children
          ) : (
            <>
              <Layout>
                <NoAuthBackdrop />
              </Layout>
            </>
          )}
        </>
      )}
    </AuthContext.Provider>
  );
}

export default AuthWrapper;
